import { useState } from "react";
import type { MouseEvent } from "react";
import { motion } from "framer-motion";
import { springs } from "@/components/ui";
import { useList } from "./ListContext";
import type { ListRowProps } from "./types";

export function ListRow({ id, children }: ListRowProps) {
  const { state, actions, meta } = useList();
  const selected = state.selectedIds.has(id);
  const [isHovered, setIsHovered] = useState(false);

  function handleCheckboxClick(e: MouseEvent) {
    e.stopPropagation();
  }

  function handleRowClick() {
    meta.onRowClick?.(id);
  }

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleRowClick();
        }
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative flex items-center gap-3 bg-card rounded-xl p-3 min-h-[48px] border border-transparent overflow-hidden transition-colors hover:border-border ${
        meta.onRowClick ? "cursor-pointer" : ""
      } ${selected ? "border-l-2 border-accent bg-accent-soft" : ""}`}
    >
      {/* Accent left bar — slides in on hover */}
      {!selected && (
        <motion.div
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent rounded-l-xl"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: isHovered ? 1 : 0 }}
          transition={springs.snappy}
          style={{ originY: 0.5 }}
        />
      )}
      <input
        type="checkbox"
        checked={selected}
        onChange={() => actions.toggle(id)}
        onClick={handleCheckboxClick}
        className="hidden sm:block h-4 w-4 shrink-0 rounded border border-border checked:bg-accent checked:border-accent"
        style={{
          accentColor: "var(--color-accent)",
        }}
      />

      <div className="flex-1 min-w-0">{children}</div>
    </motion.div>
  );
}
